import type { UserTemplate, SystemTemplate, ResolvedTemplate } from './types';

/**
 * resolveTemplate — Client-side 6-step template resolution algorithm.
 *
 * Mirrors the backend SOL-TR-002 algorithm. Called client-side so:
 * - No network round-trip per render
 * - Works offline / with cached data
 * - Template cache (TanStack Query) is the source of truth
 *
 * Priority (highest → lowest):
 *   1. User default template, matches tool exactly
 *   2. User newest active template, matches tool exactly
 *   3. System template for this tool
 *   4. User default template, universal ("*")
 *   5. User newest active template, universal ("*")
 *   6. System universal template ("*")
 *   7. Hardcoded fallback (never null)
 */
export function resolveTemplate(
  toolName: string,
  userTemplates: UserTemplate[],
  systemTemplates: SystemTemplate[],
): ResolvedTemplate {
  // Step 1: user default for exact tool
  const userDefault = userTemplates.find(
    t => t.is_active && t.is_default && t.tool_target === toolName
  );
  if (userDefault) return toUserResolved(userDefault);

  // Step 2: user newest active for exact tool (already sorted by updated_at DESC from API)
  const userNewest = userTemplates.find(
    t => t.is_active && t.tool_target === toolName
  );
  if (userNewest) return toUserResolved(userNewest);

  // Step 3: system template for exact tool
  const sysExact = systemTemplates.find(t => t.tool_target === toolName);
  if (sysExact) return toSystemResolved(sysExact);

  // Step 4: user default universal
  const userUniversalDefault = userTemplates.find(
    t => t.is_active && t.is_default && t.tool_target === '*'
  );
  if (userUniversalDefault) return toUserResolved(userUniversalDefault);

  // Step 5: user newest active universal
  const userUniversalNewest = userTemplates.find(
    t => t.is_active && t.tool_target === '*'
  );
  if (userUniversalNewest) return toUserResolved(userUniversalNewest);

  // Step 6: system universal ("*")
  const sysUniversal = systemTemplates.find(t => t.tool_target === '*');
  if (sysUniversal) return toSystemResolved(sysUniversal);

  // Step 7: hardcoded fallback (should never reach here in production since
  // system universal template is always seeded in DB)
  return {
    source:       'builtin',
    name:         'Fallback JSON Viewer',
    tool_target:  '*',
    template_html: `<pre class="tr-fallback" style="font-size:0.8125rem;overflow-x:auto;padding:0.75rem">{{json data}}</pre>`,
  };
}

// ── Converters ────────────────────────────────────────────────────────────────

function toUserResolved(t: UserTemplate): ResolvedTemplate {
  return {
    template_id:   t.id,
    source:        'user',
    name:          t.name,
    tool_target:   t.tool_target ?? '*',
    template_html: t.template_html,
    template_css:  t.template_css ?? undefined,
    template_js:   t.template_js ?? undefined,
  };
}

function toSystemResolved(t: SystemTemplate): ResolvedTemplate {
  return {
    template_id:   t.id,
    source:        'system',
    name:          t.name,
    tool_target:   t.tool_target,
    template_html: t.template_html,
    template_css:  t.template_css ?? undefined,
    template_js:   t.template_js ?? undefined,
  };
}
